import { Node } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * Collapsible section — three nested nodes mirroring the legacy Lexical
 * structure, so the title is REAL editable content (not a frozen attribute):
 *
 *   collapsible          → <details class="nula-editor-collapsible" open>
 *     collapsibleTitle   → <summary class="nula-editor-collapsible-title">  (inline*)
 *     collapsibleContent → <div class="nula-editor-collapsible-content">    (block+)
 *
 * Styling reuses the existing .nula-editor-collapsible* classes (globals.css).
 *
 * Toggle: native <details> toggle does NOT work inside ProseMirror — PM
 * intercepts clicks on its editable DOM, so the browser never flips <details>.
 * Instead, a ProseMirror plugin watches for clicks on the summary's left edge
 * (the chevron area) and toggles the container's `open` attribute via a
 * transaction, which persists + syncs the collapsed state.
 */

const CollapsibleContainer = Node.create({
  name: 'collapsible',
  group: 'block',
  content: 'collapsibleTitle collapsibleContent',
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      open: {
        default: true,
        parseHTML: (el) => el.hasAttribute('open'),
        renderHTML: (attrs) => (attrs.open ? { open: 'open' } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'details.nula-editor-collapsible' }];
  },

  renderMarkdown(node, helpers) {
    const parts = (node.content ?? [])
      .map((child) => helpers.renderChildren([child]).trim())
      .filter((part) => part !== '');
    return parts.join('\n\n');
  },

  renderHTML({ HTMLAttributes }) {
    return ['details', { ...(HTMLAttributes ?? {}), class: 'nula-editor-collapsible' }, 0];
  },

  addCommands() {
    return {
      // Local command typing — module augmentation of '@tiptap/core' doesn't
      // resolve under pnpm, so we cast at the call site instead.
      insertCollapsible:
        () =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ({ commands }: any) =>
          commands.insertContent({
            type: this.name,
            attrs: { open: true },
            content: [
              { type: 'collapsibleTitle', content: [{ type: 'text', text: 'Toggle' }] },
              { type: 'collapsibleContent', content: [{ type: 'paragraph' }] },
            ],
          }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('collapsibleToggle'),
        props: {
          handleDOMEvents: {
            // Toggle when the user clicks the summary's chevron region (the left
            // padding, before the title text). Clicking the title text itself
            // places the caret to edit, as expected.
            mousedown: (view, event) => {
              const target = event.target as HTMLElement;
              const summary = target.closest('summary.nula-editor-collapsible-title');
              if (!summary) return false;
              // Only the chevron zone (~left 24px) toggles; the rest edits the
              // title text (placing the caret).
              const rect = summary.getBoundingClientRect();
              if (event.clientX - rect.left > 24) return false;

              const details = summary.closest('details.nula-editor-collapsible');
              if (!details) return false;
              const pos = view.posAtDOM(details, 0);
              if (pos < 0) return false;
              const $pos = view.state.doc.resolve(pos);
              // Walk up to the collapsible container and flip its `open` attr.
              for (let d = $pos.depth; d >= 0; d--) {
                const node = $pos.node(d);
                if (node.type.name === 'collapsible') {
                  const nodePos = $pos.before(d);
                  event.preventDefault();
                  view.dispatch(
                    view.state.tr.setNodeMarkup(nodePos, undefined, {
                      ...node.attrs,
                      open: !node.attrs.open,
                    }),
                  );
                  return true;
                }
              }
              return false;
            },
          },
        },
      }),
    ];
  },
});

const CollapsibleTitle = Node.create({
  name: 'collapsibleTitle',
  content: 'inline*',
  defining: true,

  parseHTML() {
    return [{ tag: 'summary.nula-editor-collapsible-title' }];
  },

  renderMarkdown(node, helpers) {
    const title = helpers.renderChildren(node).trim();
    return title === '' ? '' : `**${title}**`;
  },

  renderHTML() {
    return ['summary', { class: 'nula-editor-collapsible-title' }, 0];
  },
});

const CollapsibleContent = Node.create({
  name: 'collapsibleContent',
  content: 'block+',
  defining: true,

  parseHTML() {
    return [{ tag: 'div.nula-editor-collapsible-content' }];
  },

  renderMarkdown(node, helpers) {
    return helpers.renderChildren(node, '\n\n');
  },

  renderHTML() {
    return ['div', { class: 'nula-editor-collapsible-content' }, 0];
  },
});

/** Register all three together — they're a unit. */
export const CollapsibleExtensions = [CollapsibleContainer, CollapsibleTitle, CollapsibleContent];
